/**
 * Shared Recharts theming.
 *
 * Charts were the one part of the UI that ignored the theme completely: SVG
 * attributes can't take Tailwind classes, so every chart hard-coded hex
 * strokes and rendered a white Recharts tooltip on a near-black page in dark
 * mode. Reading the same CSS variables the rest of the app uses fixes that in
 * one place, and keeps series colours on the app's five-hue palette instead of
 * Recharts' default rainbow.
 */

/** Resolve a design token to a concrete colour for SVG attributes. */
export function token(name: string, alpha?: number): string {
  if (typeof window === 'undefined') return 'currentColor';
  const raw = getComputedStyle(document.documentElement).getPropertyValue(`--${name}`).trim();
  if (!raw) return 'currentColor';
  return alpha === undefined ? `hsl(${raw})` : `hsl(${raw} / ${alpha})`;
}

/** Series palette - brand blue, then the four semantic hues, in that order. */
export const CHART_COLORS = {
  primary: 'hsl(224 76% 48%)',
  success: 'hsl(152 60% 40%)',
  warning: 'hsl(38 92% 50%)',
  danger: 'hsl(0 72% 51%)',
  muted: 'hsl(215 20% 65%)',
} as const;

/** Spread onto <Tooltip contentStyle={...} /> so it follows light/dark. */
export const chartTooltipStyle: React.CSSProperties = {
  backgroundColor: 'hsl(var(--card))',
  border: '1px solid hsl(var(--border))',
  borderRadius: '8px',
  fontSize: '12px',
  color: 'hsl(var(--foreground))',
};

/** Spread onto axis `tick` props. */
export const chartTickStyle = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' } as const;

/** className for <CartesianGrid /> - a class works here, unlike stroke hexes. */
export const chartGridClass = 'stroke-border';
